import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Home, Clock, Users, LogOut, FileBarChart, CalendarDays, CheckCircle, Menu, X, Receipt, UserCheck, FileImage, Globe, FileText, FileSpreadsheet, ShoppingCart, FileArchive, Mail, ShieldCheck, Inbox, Bot, MessageCircle, Sparkles, ClipboardList } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import QuickPhotoFAB from './QuickPhotoFAB';

const Layout = ({ user, onLogout, children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bedrijvenCount, setBedrijvenCount] = useState(null);
  const [photosCount, setPhotosCount] = useState(null);
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };

  // Fetch bedrijven count once on admin mount (and refresh on /clients route changes)
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    axiosInstance.get('/clients/all')
      .then(r => { if (!cancelled) setBedrijvenCount(Array.isArray(r.data) ? r.data.length : null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.is_admin, location.pathname]);

  // Fetch total photos count (refresh whenever the /photos route is visited or photos uploaded)
  useEffect(() => {
    if (user?.role === 'boekhouder' || user?.role === 'esquarcio') return;
    let cancelled = false;
    axiosInstance.get('/photos/summary')
      .then(r => {
        if (cancelled) return;
        const total = Array.isArray(r.data) ? r.data.reduce((s, c) => s + (c.total_photos || 0), 0) : null;
        setPhotosCount(total);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Notificatie-tellers (WhatsApp + aanvragen) — ververst bij elke routewissel
  const [notifCounts, setNotifCounts] = useState({ whatsapp_unanswered: 0, leads_new: 0 });
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const fetchCounts = () => axiosInstance.get('/notifications/counts')
      .then(r => { if (!cancelled) setNotifCounts(r.data || {}); })
      .catch(() => {});
    fetchCounts();
    const iv = setInterval(fetchCounts, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.is_admin, location.pathname]);

  const isBoekhouder = user?.role === 'boekhouder';
  const isEsquarcio = user?.role === 'esquarcio';
  const roleLabel = isEsquarcio ? 'Esquarcio' : isBoekhouder ? 'Boekhouder' : user.is_admin ? t('admin') : t('employee');
  const navItems = isEsquarcio ? [
    { path: '/esquarcio', icon: Sparkles, label: 'Esquarcio Schoonmaakbedrijf' },
  ] : isBoekhouder ? [
    { path: '/invoices', icon: FileSpreadsheet, label: 'Facturen' },
    { path: '/purchase-invoices', icon: ShoppingCart, label: 'Inkoop' },
    { path: '/expenses', icon: Receipt, label: t('expenses') },
    { path: '/accountant-export', icon: FileArchive, label: 'Boekhouder' },
    { path: '/esquarcio', icon: Sparkles, label: 'Esquarcio' },
  ] : [
    { path: '/', icon: Home, label: t('dashboard') },
    { path: '/time-entries', icon: Clock, label: t('hours') },
    { path: '/reports', icon: FileBarChart, label: t('reports') },
    { path: '/schedule', icon: CalendarDays, label: t('schedule') },
    { path: '/expenses', icon: Receipt, label: t('expenses') },
    { path: '/photos', icon: FileImage, label: t('photos'), badge: photosCount },
  ];

  if (user.is_admin) {
    navItems.push(
      { path: '/assistent', icon: Bot, label: 'Assistent' },
      { path: '/aanvragen', icon: Inbox, label: 'Aanvragen', ...(notifCounts.leads_new > 0 ? { badge: notifCounts.leads_new } : {}) },
      { path: '/whatsapp', icon: MessageCircle, label: 'WhatsApp', ...(notifCounts.whatsapp_unanswered > 0 ? { badge: notifCounts.whatsapp_unanswered } : {}) },
      { path: '/clients', icon: UserCheck, label: t('clients'), badge: bedrijvenCount },
      { path: '/approve-hours', icon: CheckCircle, label: t('approveHours') },
      { path: '/quotes', icon: FileText, label: 'Offertes' },
      { path: '/invoices', icon: FileSpreadsheet, label: 'Facturen' },
      { path: '/warranties', icon: ShieldCheck, label: 'Garanties' },
      { path: '/schade-rapporten', icon: ClipboardList, label: 'Schaderapporten' },
      { path: '/purchase-invoices', icon: ShoppingCart, label: 'Inkoop' },
      { path: '/accountant-export', icon: FileArchive, label: 'Boekhouder' },
      { path: '/esquarcio', icon: Sparkles, label: 'Esquarcio' },
      { path: '/email-templates', icon: Mail, label: 'E-mail templates' },
      { path: '/document-teksten', icon: FileText, label: 'Documentteksten' },
      { path: '/email-import', icon: Inbox, label: 'E-mail import' },
      { path: '/klantenportaal-users', icon: UserCheck, label: 'Klantenportaal' },
      { path: '/users', icon: Users, label: t('employees') }
    );
  }

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#f5f6f8]">
      {/* Top Navigation Bar - stays dark teal */}
      <nav className="bg-gradient-to-r from-[#0d3d3d] to-[#1a5555] border-b border-[#2d8585]/30 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src={isEsquarcio ? '/esquarcio-logo.jpg' : 'https://customer-assets.emergentagent.com/job_dreamy-mcnulty-2/artifacts/39goexey_JPEG-afbeelding-4738-BFE8-D0-0.jpeg'}
                alt={isEsquarcio ? 'Esquarcio Schoonmaakbedrijf' : 'Cardoso Bouw'}
                className={`h-10 object-contain ${isEsquarcio ? 'bg-white rounded px-2 py-1' : ''}`}
              />
            </div>

            <div className="hidden lg:flex space-x-0.5 overflow-x-auto flex-1 mx-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      variant="ghost"
                      size="sm"
                      className={`text-white hover:bg-white/20 transition-all duration-200 whitespace-nowrap px-2 text-sm ${
                        isActive ? 'bg-white/20 font-semibold' : ''
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-1.5" />
                      {item.label}
                      {typeof item.badge === 'number' && (
                        <span
                          className="ml-1.5 inline-flex items-center justify-center text-[10px] font-bold bg-[#3da5a5] text-white rounded-full px-1.5 py-0.5 min-w-[18px]"
                          data-testid={`nav-badge-${item.label.toLowerCase()}`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </Button>
                  </Link>
                );
              })}
            </div>

            <div className="flex items-center space-x-2">
              <Select value={i18n.language} onValueChange={changeLanguage}>
                <SelectTrigger className="w-[110px] bg-white/10 border-white/30 text-white">
                  <Globe className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nl">NL</SelectItem>
                  <SelectItem value="en">EN</SelectItem>
                  <SelectItem value="pt">PT-BR</SelectItem>
                </SelectContent>
              </Select>

              <div className="hidden sm:flex items-center space-x-3">
                <Link to="/profile">
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20">
                    <div className="text-right mr-2">
                      <p className="text-white font-semibold text-sm">{user.name}</p>
                      <p className="text-white/70 text-xs" data-testid="header-role-label">
                        {roleLabel}
                      </p>
                    </div>
                  </Button>
                </Link>
                <Button
                  data-testid="logout-button"
                  onClick={onLogout}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>

              <Button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                variant="ghost"
                size="sm"
                className="lg:hidden text-white hover:bg-white/20"
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/20 bg-[#0d3d3d] max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain">
            <div className="px-4 py-4 space-y-2 pb-24">
              <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/20">
                <div>
                  <p className="text-white font-semibold text-sm">{user.name}</p>
                  <p className="text-white/70 text-xs">
                    {isEsquarcio ? 'Esquarcio' : isBoekhouder ? 'Boekhouder' : user.is_admin ? 'Administrador' : 'Funcionario'}
                  </p>
                </div>
                <Button
                  onClick={onLogout}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                  data-testid="mobile-logout-button"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="ml-2">Sair</span>
                </Button>
              </div>

              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={handleNavClick}>
                    <Button
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      variant="ghost"
                      className={`w-full justify-start text-white hover:bg-white/20 transition-all duration-200 ${
                        isActive ? 'bg-white/20' : ''
                      }`}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.label}
                      {typeof item.badge === 'number' && (
                        <span className="ml-auto inline-flex items-center justify-center text-[10px] font-bold bg-[#3da5a5] text-white rounded-full px-1.5 py-0.5 min-w-[18px]">
                          {item.badge}
                        </span>
                      )}
                    </Button>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content - Light background with logo watermark */}
      <main className={`page-content ${isEsquarcio ? "esquarcio" : ""} max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-20 relative z-10`} style={isEsquarcio ? { '--esq-watermark': `url(${process.env.PUBLIC_URL || ''}/esquarcio-logo.jpg)` } : undefined}>
        {children}
      </main>

      {/* Floating Quick Photo Button - visible on all authenticated pages */}
      {!isBoekhouder && !isEsquarcio && <QuickPhotoFAB />}
    </div>
  );
};

export default Layout;
